import React, { useEffect, useRef, useState } from 'react';
import { Loader } from '@googlemaps/js-api-loader';
import { Map } from 'lucide-react';

// Google Maps type declarations to prevent compile/linter warnings
declare const google: any;

declare global {
  interface Window {
    google: any;
  }
}

interface Service {
  id: number;
  title: string;
  description: string;
  price: number;
  duration: string;
  category: string;
  worker: {
    id: number;
    name: string;
    parish: string;
    rating: number;
    totalReviews: number;
  };
}

interface ServiceMapProps {
  services?: Service[];
  // Individual location props for single location display
  address?: string;
  parish?: string;
  city?: string;
  country?: string;
  latitude?: number;
  longitude?: number;
  workerName?: string;
  height?: string;
  className?: string;
  isAuthenticated?: boolean;
  onRestrictedAction?: () => void;
}

// Jamaica parish coordinates mapping
const JAMAICA_PARISHES: { [key: string]: { lat: number; lng: number } } = {
  'Kingston': { lat: 18.0179, lng: -76.8099 },
  'St. Andrew': { lat: 18.0747, lng: -76.7949 },
  'St. Thomas': { lat: 17.9857, lng: -76.2259 },
  'Portland': { lat: 18.1747, lng: -76.4591 },
  'St. Mary': { lat: 18.3679, lng: -76.9668 },
  'St. Ann': { lat: 18.4147, lng: -77.1998 },
  'Trelawny': { lat: 18.3895, lng: -77.6040 },
  'St. James': { lat: 18.4762, lng: -77.9203 },
  'Hanover': { lat: 18.4239, lng: -78.1203 },
  'Westmoreland': { lat: 18.3070, lng: -78.1421 },
  'St. Elizabeth': { lat: 17.9036, lng: -77.6652 },
  'Manchester': { lat: 18.0534, lng: -77.5088 },
  'Clarendon': { lat: 17.9654, lng: -77.2371 },
  'St. Catherine': { lat: 17.9712, lng: -76.9635 }
};

const ServiceMap: React.FC<ServiceMapProps> = ({
  services = [],
  address,
  parish,
  city,
  country,
  latitude,
  longitude,
  workerName,
  height = '100%',
  className = '',
  isAuthenticated = false,
  onRestrictedAction
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const apiKeycehck = 'AIzaSyD5ZwymW1HDabeyBUPwgTaZHoIWmLNvCFA';
  useEffect(() => {
    const initMap = async () => {
      const apiKey = 'AIzaSyD5ZwymW1HDabeyBUPwgTaZHoIWmLNvCFA';

      if (!apiKey) {
        console.warn('Google Maps API key not found. Please set REACT_APP_GOOGLE_MAPS_API_KEY in your .env file');
        return;
      }

      try {
        const loader = new Loader({
          apiKey: apiKey,
          version: 'weekly',
          libraries: ['places', 'geometry']
        });

        await loader.load();

        if (mapRef.current) {
          // Center map on Jamaica
          const jamaicaCenter = { lat: 18.1096, lng: -77.2975 };

          const googleMap = new google.maps.Map(mapRef.current, {
            center: jamaicaCenter,
            zoom: 9,
            styles: [
              {
                featureType: 'poi',
                elementType: 'labels',
                stylers: [{ visibility: 'off' }]
              },
              {
                featureType: 'transit',
                elementType: 'labels',
                stylers: [{ visibility: 'off' }]
              }
            ],
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: true,
            zoomControl: true
          });

          setMap(googleMap);
          setIsLoaded(true);
        }
      } catch (error) {
        console.error('Error loading Google Maps:', error);
      }
    };

    initMap();
  }, []);

  useEffect(() => {
    if (!map || !isLoaded) return;

    // Function to geocode address (defined inside useEffect to avoid dependency issues)
    const geocodeAddress = (address: string): Promise<google.maps.LatLng | null> => {
      return new Promise((resolve) => {
        if (!address || !map) {
          resolve(null);
          return;
        }

        const geocoder = new google.maps.Geocoder();

        // First try to geocode the original address (supports any global address like India, USA, Jamaica etc.)
        geocoder.geocode({ address: address }, (results: any, status: any) => {
          if (status === 'OK' && results && results[0]) {
            resolve(results[0].geometry.location);
          } else {
            // Fallback: Enhance address with Jamaica context for better geocoding if original fails
            const searchAddress = `${address}, Jamaica`;
            geocoder.geocode({ address: searchAddress }, (resultsSecondary: any, statusSecondary: any) => {
              if (statusSecondary === 'OK' && resultsSecondary && resultsSecondary[0]) {
                resolve(resultsSecondary[0].geometry.location);
              } else {
                console.warn(`Geocoding failed for address: ${address}. Status: ${statusSecondary}`);
                resolve(null);
              }
            });
          }
        });
      });
    };

    // Handle services array (multiple services)
    if (services && services.length > 0) {
      // Clear existing markers
      // Note: In a real app, you'd want to store marker references to clear them properly

      // Group services by parish to avoid duplicate markers
      const servicesByParish = services.reduce((acc, service) => {
        const parish = service.worker.parish;
        if (!acc[parish]) {
          acc[parish] = [];
        }
        acc[parish].push(service);
        return acc;
      }, {} as { [key: string]: Service[] });

      // Add markers for each parish with services
      Object.entries(servicesByParish).forEach(([parish, parishServices]) => {
        const coordinates = JAMAICA_PARISHES[parish];

        if (coordinates) {
          // Create custom marker with service count
          const marker = new google.maps.Marker({
            position: coordinates,
            map: map,
            title: `${parish} - ${parishServices.length} service(s)`,
            icon: {
              url: 'data:image/svg+xml,' + encodeURIComponent(`
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="40" viewBox="0 0 32 40">
                  <path d="M16 0C7.2 0 0 7.2 0 16c0 8.8 16 24 16 24s16-15.2 16-24C32 7.2 24.8 0 16 0z" fill="#4285F4"/>
                  <circle cx="16" cy="16" r="8" fill="white"/>
                  <text x="16" y="20" text-anchor="middle" fill="#4285F4" font-family="Arial" font-size="10" font-weight="bold">${parishServices.length}</text>
                </svg>
              `),
              scaledSize: new google.maps.Size(32, 40),
              anchor: new google.maps.Point(16, 40)
            }
          });

          // Create info window content
          const infoContent = `
            <div style="max-width: 300px; padding: 10px;">
              <h3 style="margin: 0 0 10px 0; color: #333; font-size: 16px;">${parish}</h3>
              <p style="margin: 0 0 10px 0; color: #666; font-size: 14px;">${parishServices.length} service(s) available</p>
              <div style="max-height: 200px; overflow-y: auto;">
                ${parishServices.map(service => `
                  <div style="border-bottom: 1px solid #eee; padding: 8px 0;">
                    <div style="font-weight: bold; color: #333; font-size: 14px;">${service.title}</div>
                    <div style="color: #666; font-size: 12px; margin: 2px 0;">By ${service.worker.name}</div>
                    <div style="color: #4285F4; font-weight: bold; font-size: 13px;">$${service.price}</div>
                    <div style="color: #888; font-size: 11px;">${service.category} • ${service.duration}</div>
                    <div style="color: #ff9800; font-size: 11px;">
                      ⭐ ${service.worker.rating.toFixed(1)} (${service.worker.totalReviews} reviews)
                    </div>
                  </div>
                `).join('')}
              </div>
            </div>
          `;

          const infoWindow = new google.maps.InfoWindow({
            content: infoContent
          });

          marker.addListener('click', () => {
            infoWindow.open(map, marker);
          });
        }
      });

      // Adjust map bounds to show all markers if there are services
      if (Object.keys(servicesByParish).length > 0) {
        const bounds = new google.maps.LatLngBounds();
        Object.keys(servicesByParish).forEach(parish => {
          const coordinates = JAMAICA_PARISHES[parish];
          if (coordinates) {
            bounds.extend(coordinates);
          }
        });
        map.fitBounds(bounds);

        // Ensure minimum zoom level
        const listener = google.maps.event.addListener(map, 'idle', () => {
          if (map.getZoom() && map.getZoom()! > 12) {
            map.setZoom(12);
          }
          google.maps.event.removeListener(listener);
        });
      }
    }
    // Handle individual location (single location marker)
    if (latitude && longitude) {
      const coordinates = { lat: latitude, lng: longitude };

      const marker = new google.maps.Marker({
        position: coordinates,
        map: map,
        title: workerName || address || 'Service Location',
        icon: {
          url: 'data:image/svg+xml,' + encodeURIComponent(`
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="40" viewBox="0 0 32 40">
              <path d="M16 0C7.2 0 0 7.2 0 16c0 8.8 16 24 16 24s16-15.2 16-24C32 7.2 24.8 0 16 0z" fill="#DC2626"/>
              <circle cx="16" cy="16" r="8" fill="white"/>
              <text x="16" y="20" text-anchor="middle" fill="#DC2626" font-family="Arial" font-size="8" font-weight="bold">📍</text>
            </svg>
          `),
          scaledSize: new google.maps.Size(32, 40),
          anchor: new google.maps.Point(16, 40)
        }
      });

      // Create info window
      const infoContent = `
        <div style="max-width: 300px; padding: 12px;">
          <h3 style="margin: 0 0 10px 0; color: #333; font-size: 16px;">${workerName || 'Service Location'}</h3>
          <div style="color: #666; font-size: 14px; line-height: 1.5;">
            ${address ? `<div style="margin-bottom: 8px;"><strong>📍 Address:</strong><br/>${address}</div>` : ''}
            <div style="margin-top: 10px; padding-top: 8px; border-top: 1px solid #eee; font-size: 12px; color: #888;">
              <strong>Coordinates:</strong><br/>
              Lat: ${latitude.toFixed(6)}<br/>
              Lng: ${longitude.toFixed(6)}
            </div>
          </div>
        </div>
      `;

      const infoWindow = new google.maps.InfoWindow({
        content: infoContent
      });

      marker.addListener('click', () => {
        infoWindow.open(map, marker);
      });

      // Center the map on this location
      map.setCenter(coordinates);
      map.setZoom(15);
    }
    // Handle address geocoding fallback
    else if (address || parish) {
      const handleLocationMarker = async () => {
        let coordinates: google.maps.LatLng | { lat: number; lng: number } | null = null;

        // First try to geocode the actual address
        if (address) {
          coordinates = await geocodeAddress(address);
        }

        // Fallback to parish coordinates if geocoding fails
        if (!coordinates && parish) {
          const parishCoords = JAMAICA_PARISHES[parish];
          if (parishCoords) {
            coordinates = parishCoords;
          } else {
            // Geocode the parish name itself if it's not in the predefined JAMAICA_PARISHES map
            coordinates = await geocodeAddress(parish);
          }
        }

        if (coordinates) {
          // Create a single marker for the location
          const marker = new google.maps.Marker({
            position: coordinates,
            map: map,
            title: address || `${parish}${city ? `, ${city}` : ''}`,
            icon: {
              url: 'data:image/svg+xml,' + encodeURIComponent(`
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="40" viewBox="0 0 32 40">
                  <path d="M16 0C7.2 0 0 7.2 0 16c0 8.8 16 24 16 24s16-15.2 16-24C32 7.2 24.8 0 16 0z" fill="#DC2626"/>
                  <circle cx="16" cy="16" r="8" fill="white"/>
                  <text x="16" y="20" text-anchor="middle" fill="#DC2626" font-family="Arial" font-size="8" font-weight="bold">📍</text>
                </svg>
              `),
              scaledSize: new google.maps.Size(32, 40),
              anchor: new google.maps.Point(16, 40)
            }
          });

          // Create info window for single location
          const infoContent = `
            <div style="max-width: 300px; padding: 12px;">
              <h3 style="margin: 0 0 10px 0; color: #333; font-size: 16px;">Service Location</h3>
              <div style="color: #666; font-size: 14px; line-height: 1.5;">
                ${address ? `<div style="margin-bottom: 8px;"><strong>📍 Address:</strong><br/>${address}</div>` : ''}
                ${parish ? `<div style="margin-bottom: 8px;"><strong>🏛️ Parish:</strong> ${parish}</div>` : ''}
                ${city ? `<div style="margin-bottom: 8px;"><strong>🏙️ City:</strong> ${city}</div>` : ''}
                ${country ? `<div style="margin-bottom: 8px;"><strong>🌍 Country:</strong> ${country}</div>` : ''}
                ${coordinates ?
              `<div style="margin-top: 10px; padding-top: 8px; border-top: 1px solid #eee; font-size: 12px; color: #888;">
                    <strong>Coordinates:</strong><br/>
                    Lat: ${(typeof (coordinates as any).lat === 'function' ? (coordinates as any).lat() : (coordinates as any).lat).toFixed(6)}<br/>
                    Lng: ${(typeof (coordinates as any).lng === 'function' ? (coordinates as any).lng() : (coordinates as any).lng).toFixed(6)}
                  </div>` : ''
            }
              </div>
            </div>
          `;

          const infoWindow = new google.maps.InfoWindow({
            content: infoContent
          });

          marker.addListener('click', () => {
            infoWindow.open(map, marker);
          });

          // Center the map on this location
          map.setCenter(coordinates);
          map.setZoom(address ? 15 : 11); // Zoom closer if we have an exact address
        }
      };

      handleLocationMarker();
    }

  }, [map, isLoaded, services, address, parish, city, country, latitude, longitude, workerName]);

  return (
    <div
      className={className}
      style={{
        width: '100%',
        height: height,
        minHeight: height,
        position: 'relative'
      }}
    >
      {!apiKeycehck ? (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          background: '#f5f5f5',
          color: '#666',
          textAlign: 'center',
          padding: '20px'
        }}>
          <div>
            <p>📍 Map Preview</p>
            <p style={{ fontSize: '12px' }}>Google Maps API key required</p>
            <img
              src="/assets/images/map.png"
              alt="Map placeholder"
              style={{ maxWidth: '100%', height: 'auto', opacity: 0.7 }}
            />
          </div>
        </div>
      ) : (
        <div
          ref={mapRef}
          style={{
            width: '100%',
            height: '100%',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}
        />
      )}

      {/* Show service count for services array */}
      {services && services.length > 0 && (
        <div style={{
          position: 'absolute',
          top: '10px',
          left: '10px',
          background: 'rgba(255,255,255,0.9)',
          padding: '8px 12px',
          borderRadius: '4px',
          fontSize: '12px',
          color: '#333',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          📍 {services.length} service{services.length !== 1 ? 's' : ''} available
        </div>
      )}

      {/* Show location info for single location */}
      {isAuthenticated && !services?.length && (address || parish) && (
        <div style={{
          position: 'absolute',
          top: '10px',
          left: '10px',
          background: 'rgba(255,255,255,0.9)',
          padding: '8px 12px',
          borderRadius: '4px',
          fontSize: '12px',
          color: '#333',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          📍 {address || `${parish}${city ? `, ${city}` : ''}`}
        </div>
      )}

      {/* Open in Google Maps Button */}
      {!services?.length && (latitude || longitude || address || parish) && (
        <button
          type="button"
          onClick={() => {
            if (!isAuthenticated && onRestrictedAction) {
              onRestrictedAction();
              return;
            }
            let destination = '';
            if (latitude && longitude) {
              destination = `${latitude},${longitude}`;
            } else if (address) {
              destination = encodeURIComponent(`${address}, Jamaica`);
            } else if (parish) {
              destination = encodeURIComponent(`${parish}, Jamaica`);
            }

            if (destination) {
              const url = `https://www.google.com/maps/dir/?api=1&destination=${destination}`;
              window.open(url, '_blank');
            }
          }}
          style={{
            position: 'absolute',
            bottom: '24px',
            right: '16px',
            backgroundColor: '#fff',
            color: '#1a73e8',
            border: '1px solid #dadce0',
            borderRadius: '8px',
            padding: '10px 16px',
            fontSize: '14px',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            cursor: 'pointer',
            boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
            zIndex: 5,
            transition: 'all 0.2s ease',
            outline: 'none'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.backgroundColor = '#f8f9fa';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.3)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = '#fff';
            e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.3)';
          }}
        >
          <Map size={16} />
          Get Directions
        </button>
      )}
    </div>
  );
};

export default ServiceMap; 